import {
  Directive,
  ElementRef,
  OnDestroy,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';
import { TourService } from './tour.service';

/**
 * Marks an element as a guided-tour step. Use with `TourService.start()` or
 * `TourService.highlight()`.
 *
 * When `tourTour` is empty, the step is not registered (useful for route-bound tours).
 */
@Directive({
  selector: '[tcTourStep], [tourStep]',
  standalone: true,
  host: {
    '[attr.data-tc-tour-step]': 'tcTourStep()',
  },
})
export class TourStepDirective implements OnDestroy {
  private readonly tour = inject(TourService);
  private readonly elRef = inject(ElementRef<HTMLElement>);
  private registeredId: string | null = null;

  readonly tcTourStep = input.required<string>({ alias: 'tourStep' });
  readonly tourTitle = input<string>('');
  readonly tourContent = input<string>('');
  readonly tourOrder = input<number>(0);
  /** Tour group id — empty = not registered. */
  readonly tourTour = input<string>('default');

  constructor() {
    effect(() => {
      this.tcTourStep();
      this.tourTour();
      this.tourTitle();
      this.tourContent();
      this.tourOrder();
      untracked(() => this.syncRegistration());
    });
  }

  ngOnDestroy(): void {
    this.unregister();
  }

  private syncRegistration(): void {
    const id = this.tcTourStep();
    const tourId = this.tourTour();

    if (!tourId) {
      this.unregister();
      return;
    }

    if (this.registeredId && this.registeredId !== id) {
      this.tour.unregister(this.registeredId);
      this.registeredId = null;
    }

    this.tour.register({
      id,
      element: this.elRef.nativeElement,
      title: this.tourTitle() || undefined,
      content: this.tourContent() || undefined,
      order: this.tourOrder(),
      tourId,
    });
    this.registeredId = id;
  }

  private unregister(): void {
    if (!this.registeredId) {
      return;
    }
    this.tour.unregister(this.registeredId);
    this.registeredId = null;
  }
}
